import { NavLink, useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { UserRole } from '@/types/enums'
import { cn, getInitials } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import {
  LayoutDashboard, Ticket, KanbanSquare, MessageSquare, BookmarkPlus,
  Package, KeyRound, Users, UserCog, Mail, Route, Tags, BarChart3,
  LogOut, ChevronLeft, X, Inbox, ClipboardList,
} from 'lucide-react'

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
}

function getNavItems(role: UserRole): NavItem[] {
  switch (role) {
    case UserRole.Client:
      return [
        { label: 'Dashboard', href: '/client/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
        { label: 'My Tickets', href: '/client/tickets', icon: <Ticket className="w-4 h-4" /> },
        { label: 'My Products', href: '/client/products', icon: <Package className="w-4 h-4" /> },
        { label: 'New Ticket', href: '/client/tickets/new', icon: <BookmarkPlus className="w-4 h-4" /> },
      ]
    case UserRole.Agent:
      return [
        { label: 'Dashboard', href: '/agent/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
        { label: 'Tickets', href: '/agent/tickets', icon: <Ticket className="w-4 h-4" /> },
        { label: 'Kanban', href: '/agent/tickets/kanban', icon: <KanbanSquare className="w-4 h-4" /> },
        { label: 'Enquiries', href: '/agent/enquiries', icon: <Inbox className="w-4 h-4" /> },
        { label: 'Saved Replies', href: '/agent/saved-replies', icon: <MessageSquare className="w-4 h-4" /> },
      ]
    case UserRole.Admin:
      return [
        { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
        { label: 'Tickets', href: '/admin/tickets', icon: <Ticket className="w-4 h-4" /> },
        { label: 'Products', href: '/admin/products', icon: <Package className="w-4 h-4" /> },
        { label: 'Serial Codes', href: '/admin/serial-codes', icon: <KeyRound className="w-4 h-4" /> },
        { label: 'Registered Products', href: '/admin/registered-products', icon: <ClipboardList className="w-4 h-4" /> },
        { label: 'Clients', href: '/admin/clients', icon: <Users className="w-4 h-4" /> },
        { label: 'Agents', href: '/admin/agents', icon: <UserCog className="w-4 h-4" /> },
        { label: 'Enquiries', href: '/admin/enquiries', icon: <Inbox className="w-4 h-4" /> },
        { label: 'Email Templates', href: '/admin/email-templates', icon: <Mail className="w-4 h-4" /> },
        { label: 'Assignment Rules', href: '/admin/assignment-rules', icon: <Route className="w-4 h-4" /> },
        { label: 'Saved Replies', href: '/admin/saved-replies', icon: <MessageSquare className="w-4 h-4" /> },
        { label: 'Tags', href: '/admin/tags', icon: <Tags className="w-4 h-4" /> },
        { label: 'Reports', href: '/admin/reports', icon: <BarChart3 className="w-4 h-4" /> },
      ]
  }
}

export function Sidebar() {
  const { profile, reset } = useAuthStore()
  const { sidebarOpen, sidebarCollapsed, setSidebarOpen, toggleSidebarCollapse } = useUIStore()
  const navigate = useNavigate()

  if (!profile) return null

  const navItems = getNavItems(profile.role as UserRole)

  async function handleLogout() {
    await supabase.auth.signOut()
    reset()
    navigate('/login')
  }

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 h-full bg-surface-900 text-white z-50 flex flex-col',
          'transition-all duration-200 ease-out',
          sidebarCollapsed ? 'w-16' : 'w-60',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0 lg:relative'
        )}
      >
        {/* Header */}
        <div className={cn(
          'h-14 flex items-center border-b border-surface-800 shrink-0',
          sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'
        )}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-primary-600 flex items-center justify-center">
                <Ticket className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-bold tracking-tight">Ticket CRM</span>
            </div>
          )}
          <button
            onClick={() => {
              if (window.innerWidth < 1024) setSidebarOpen(false)
              else toggleSidebarCollapse()
            }}
            className="text-surface-400 hover:text-white transition-colors p-1 rounded"
            aria-label="Toggle sidebar"
          >
            {window.innerWidth < 1024 ? (
              <X className="w-4 h-4" />
            ) : (
              <ChevronLeft className={cn('w-4 h-4 transition-transform', sidebarCollapsed && 'rotate-180')} />
            )}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => cn(
                'flex items-center gap-3 h-9 rounded-md text-sm font-medium transition-colors',
                sidebarCollapsed ? 'justify-center px-0' : 'px-3',
                isActive
                  ? 'bg-primary-600/20 text-primary-400'
                  : 'text-surface-400 hover:text-white hover:bg-surface-800'
              )}
            >
              {item.icon}
              {!sidebarCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer — user */}
        <div className={cn(
          'border-t border-surface-800 p-3',
          sidebarCollapsed ? 'flex justify-center' : ''
        )}>
          {sidebarCollapsed ? (
            <button
              onClick={handleLogout}
              className="text-surface-400 hover:text-white p-1.5 rounded transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-primary-700 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold">{getInitials(profile.full_name)}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{profile.full_name}</p>
                  <p className="text-[10px] text-surface-500 capitalize">{profile.role}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="text-surface-500 hover:text-white p-1 rounded transition-colors shrink-0"
                aria-label="Logout"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
